// What a system looks like on the map (spec 15c §3.2–3.3): relation colour, own markers, G-66
// alliance rings and the label text per zoom tier. Pure, so it is unit-tested without Pixi.
import type { MapSystem } from '@stars-and-ships/api-types';
import { MAP_COLORS } from '../theme';
import type { ZoomTier } from './zoomTier';

export function systemColor(
  system: MapSystem,
  playerId: string | undefined,
  allianceId?: string | null
): number {
  if (!system.owner) return MAP_COLORS.unowned;
  if (system.owner.playerId === playerId) return MAP_COLORS.own;
  if (allianceId && system.owner.alliance?.id === allianceId) return MAP_COLORS.ally;
  return MAP_COLORS.player;
}

export type OwnMarker = 'home' | 'colony' | null;

export function ownMarker(
  systemId: string,
  homeSystemId: string | null,
  ownSystemIds: ReadonlySet<string>
): OwnMarker {
  if (systemId === homeSystemId) return 'home';
  return ownSystemIds.has(systemId) ? 'colony' : null;
}

/** G-66 (b): a foreign alliance's emblem colour; own and allied systems never get a ring. */
export function allianceRingColor(
  system: MapSystem,
  playerId: string | undefined,
  allianceId: string | null | undefined
): number | null {
  const alliance = system.owner?.alliance;
  if (!alliance?.emblem || system.owner?.playerId === playerId) return null;
  if (allianceId && alliance.id === allianceId) return null;
  return Number.parseInt(alliance.emblem.color.slice(1), 16);
}

export interface LabelLines {
  title: string | null;
  owner: string | null;
}

/**
 * P25: far — own system names and (Tim, 15c feedback round 2) only the `[TAG]` of other commanders'
 * alliances; mid — every name with `[TAG]`; close — the owner as well. An owned system's points
 * follow its name: "Kestrel (42) [RED]" (Tim, 16c feedback).
 */
export function labelLines(
  system: MapSystem,
  tier: ZoomTier,
  playerId: string | undefined
): LabelLines {
  const own = playerId !== undefined && system.owner?.playerId === playerId;
  const alliance = system.owner?.alliance;
  const name = system.owner ? `${system.name} (${system.points})` : system.name;
  if (tier === 'far') {
    if (own) return { title: name, owner: null };
    return { title: alliance ? `[${alliance.tag}]` : null, owner: null };
  }
  const tag = alliance ? ` [${alliance.tag}]` : '';
  const title = `${name}${tag}`;
  if (tier === 'mid' || !system.owner) return { title, owner: null };
  return { title, owner: own ? 'You' : system.owner.displayName };
}
